import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Pencil, Users, KeyRound, ChevronDown, Loader2, Phone, Mail } from 'lucide-react';
import api from '../lib/api';
import { useAuthStore } from '../lib/auth';
import { Button } from '../components/ui/button';
import { SubcoLogo, TagChip } from '../components/subco/SubcoBits';
import { SubcoCompanyDialog } from '../components/subco/SubcoCompanyDialog';

function Employees({ id }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get(`/subco-companies/${id}/employees`).then((r) => setRows(r.data.data)).catch(() => setRows([])); }, [id]);
  if (!rows) return <Loader2 className="h-4 w-4 animate-spin text-slate-400 m-3" />;
  if (!rows.length) return <p className="text-xs text-slate-400 p-3">Henüz çalışan bağlanmamış — Kullanıcılar ekranından ekleyin.</p>;
  return (
    <ul className="divide-y divide-slate-100 dark:divide-slate-800" data-testid={`subco-company-employees-${id}`}>
      {rows.map((e) => (
        <li key={e.id} className="px-3 py-2 flex items-center gap-2 text-xs">
          <span className="font-semibold flex-1 truncate">{e.full_name}</span>
          <span className="text-slate-500 truncate">{e.firm_name || '—'}</span>
          {(e.tags || []).map((t) => <TagChip key={t} tag={t} active />)}
          {e.status !== 'ACTIVE' && <span className="text-[10px] text-rose-600 font-bold">{e.status}</span>}
        </li>
      ))}
    </ul>
  );
}

function Card({ s, onEdit, canEdit }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`rounded-2xl border bg-white dark:bg-slate-900 overflow-hidden ${s.active ? 'border-slate-200 dark:border-slate-800' : 'border-dashed border-slate-300 opacity-60'}`} data-testid={`subco-company-card-${s.id}`}>
      <div className="p-4 flex gap-3">
        <SubcoLogo subco={s} size="h-12 w-12" text="text-base" />
        <div className="flex-1 min-w-0 space-y-1">
          <div className="flex items-center gap-2"><h3 className="font-black truncate">{s.name}</h3>{!s.active && <span className="text-[10px] font-bold text-slate-500">PASİF</span>}</div>
          <div className="flex flex-wrap gap-1">{s.firms.map((f) => <span key={f.id} className="rounded bg-indigo-50 text-indigo-700 px-1.5 py-0.5 text-[10px] font-semibold">{f.name}</span>)}{!s.firms.length && <span className="text-[10px] text-slate-400">Firma seçilmemiş</span>}</div>
          <div className="text-[11px] text-slate-500 flex flex-wrap gap-x-3">
            {s.contact_name && <span>{s.contact_name}</span>}
            {s.phone && <span className="inline-flex items-center gap-0.5"><Phone className="h-3 w-3" />{s.phone}</span>}
            {s.email && <span className="inline-flex items-center gap-0.5"><Mail className="h-3 w-3" />{s.email}</span>}
          </div>
        </div>
        {canEdit && <button type="button" onClick={() => onEdit(s)} className="self-start p-1.5 rounded-md text-slate-400 hover:text-slate-900 hover:bg-slate-100" data-testid={`subco-company-edit-${s.id}`}><Pencil className="h-4 w-4" /></button>}
      </div>
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full border-t border-slate-100 dark:border-slate-800 px-4 py-2 flex items-center gap-3 text-xs text-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800" data-testid={`subco-company-toggle-${s.id}`}>
        <span className="inline-flex items-center gap-1 font-semibold"><Users className="h-3.5 w-3.5" />{s.employee_count} çalışan</span>
        <span className="inline-flex items-center gap-1"><KeyRound className="h-3.5 w-3.5" />{s.logins.length} giriş hesabı</span>
        <ChevronDown className={`h-3.5 w-3.5 ml-auto transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <Employees id={s.id} />}
    </div>
  );
}

export default function SubcoCompanies() {
  const role = useAuthStore((st) => st.user?.role);
  const [items, setItems] = useState(null);
  const [firms, setFirms] = useState([]);
  const [dialog, setDialog] = useState({ open: false, subco: null });
  const load = useCallback(() => api.get('/subco-companies', { params: { include_inactive: true } }).then((r) => setItems(r.data.data)).catch((e) => toast.error(e?.response?.data?.detail || 'Yüklenemedi')), []);
  useEffect(() => { load(); api.get('/admin/firms').then((r) => setFirms(r.data?.data || [])).catch(() => {}); }, [load]);
  const canEdit = role === 'SUPER_ADMIN';
  return (
    <div className="space-y-4" data-testid="subco-companies-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <p className="text-sm text-slate-500 max-w-xl">Taşeron firmalar. Kullanıcı eklerken subco seçilir; çalışan subco'nun çalıştığı firmalardan birine bağlanır ve Planlama Yap → Taşeron sekmesinde subco adı altında görünür.</p>
        {canEdit && <Button onClick={() => setDialog({ open: true, subco: null })} data-testid="subco-company-add-btn"><Plus className="h-4 w-4 mr-1" />Yeni Subco</Button>}
      </div>
      {!items ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : items.length === 0 ? <p className="text-sm text-slate-400 py-10 text-center" data-testid="subco-company-empty">Henüz subco yok</p> : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">{items.map((s) => <Card key={s.id} s={s} canEdit={canEdit} onEdit={(x) => setDialog({ open: true, subco: x })} />)}</div>
      )}
      <SubcoCompanyDialog open={dialog.open} subco={dialog.subco} firms={firms} onClose={() => setDialog({ open: false, subco: null })} onSaved={() => { setDialog({ open: false, subco: null }); load(); }} />
    </div>
  );
}
